import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, collection, doc, setDoc, addDoc, 
  onSnapshot, query, deleteDoc, orderBy, writeBatch 
} from 'firebase/firestore';
import { 
  getAuth, signInAnonymously, onAuthStateChanged 
} from 'firebase/auth';
import { 
  Home, Info, Briefcase, Mail, Lock, 
  ChevronRight, ExternalLink, Menu, X, Globe, 
  TrendingUp, Users, BookOpen, CheckCircle, Trash2, Edit2, Plus, ArrowRight,
  Award, BarChart3, GraduationCap, Quote, Calendar, User, Clock, AlertCircle, RefreshCw, Layout,
  Bold, Italic, List, AlignLeft, AlignCenter, AlignRight
} from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyClnJ-sKpNBEeRIzu8nit4oKAksf6Ya6zk",
  authDomain: "edvansolutionsv1.firebaseapp.com",
  projectId: "edvansolutionsv1",
  storageBucket: "edvansolutionsv1.firebasestorage.app",
  messagingSenderId: "781357606956",
  appId: "1:781357606956:web:d6806d67dd2533880e8989"
};

// Initialize Firebase
let app, auth, db;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
} catch (error) {
  console.error("Firebase initialization error. Did you paste the config?", error);
}

const appId = 'edvansolutions-live-v1';

// --- Default Data ---
const DEFAULT_CONTENT = {
  about: {
    title: "About Edvansolutions",
    description: "<p>Edvansolutions is a premier financial consultancy and ed-tech firm bridging the gap between academic knowledge and real-world application. We offer global Virtual CFO services with a strong presence across India, Hong Kong, UK, UAE, Africa, and the United States.</p><p><br></p><p>Our mission is to empower the next generation of finance professionals by providing unparalleled exposure to real-world CFO challenges. We manage global clients on live projects, shaping business outcomes across diverse industries and geographies.</p>",
    mission: "To empower finance professionals and businesses through strategic financial leadership.",
    stat1_val: "500+",
    stat1_lbl: "Students Trained",
    stat2_val: "5+",
    stat2_lbl: "Global Hubs",
    stat3_val: "30+",
    stat3_lbl: "Corporate Partners",
    stat4_val: "24/7",
    stat4_lbl: "Support"
  },
  contact: {
    email: "info@edvansolutions.com",
    phone: "+91 123 456 7890",
    address: "Global Presence: India, Hong Kong, UK, UAE, USA",
  }
};

const DEFAULT_SERVICES = [
  {
    id: 'svc_1',
    title: "Virtual CFO Services",
    description: "Strategic financial guidance and C-suite decision support for businesses. We provide comprehensive financial leadership.",
    icon: "TrendingUp"
  },
  {
    id: 'svc_2',
    title: "Finance Associate Program",
    description: "Two-Year Global CFO Program for CMA, ACCA, and CPA professionals. Gain hands-on mastery in financial reporting.",
    icon: "Users"
  },
  {
    id: 'svc_3',
    title: "Corporate Training",
    description: "Tailored financial training modules for corporate teams to enhance financial literacy and strategic thinking.",
    icon: "GraduationCap"
  },
  {
    id: 'svc_4',
    title: "Compliance & Regulatory",
    description: "Navigate complex compliance and international financial standards with our expert team.",
    icon: "CheckCircle"
  }
];

const DEFAULT_BLOGS = [
  {
    id: 'blog_1',
    title: "The Future of Virtual CFOs",
    excerpt: "Why modern businesses are shifting towards virtual financial leadership to drive growth and efficiency.",
    content: "Full content here...",
    date: "Oct 24, 2023",
    author: "Admin"
  },
  {
    id: 'blog_2',
    title: "Mastering Financial Analysis",
    excerpt: "Key strategies for finance professionals to deliver actionable insights from raw data.",
    content: "Full content here...",
    date: "Nov 15, 2023",
    author: "Admin"
  }
];

const SAMPLE_TESTIMONIALS = [
  {
    name: "Sarah J.",
    role: "CFO, TechStart Inc.",
    text: "Edvansolutions transformed our financial reporting structure. Their virtual CFO service is indistinguishable from an in-house expert."
  },
  {
    name: "David L.",
    role: "CMA Aspirant",
    text: "The Finance Associate Program gave me the practical exposure I was missing. I landed a senior role immediately after completion."
  },
  {
    name: "Global Retail Co.",
    role: "Corporate Client",
    text: "Their compliance team navigated us through complex international regulations seamlessly. Highly recommended."
  }
];

// --- Components ---

// --- Simple Rich Text Editor ---
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const contentRef = useRef(null);

  // Sync initial value
  useEffect(() => {
    if (contentRef.current && value && contentRef.current.innerHTML !== value) {
        contentRef.current.innerHTML = value;
    }
  }, [value]); 

  const handleCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (contentRef.current) onChange(contentRef.current.innerHTML);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button type="button" onClick={() => handleCommand('bold')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Bold"><Bold size={16}/></button>
        <button type="button" onClick={() => handleCommand('italic')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Italic"><Italic size={16}/></button>
        <div className="w-px h-4 bg-gray-300 mx-1"></div>
        <button type="button" onClick={() => handleCommand('insertUnorderedList')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Bullet List"><List size={16}/></button>
        <div className="w-px h-4 bg-gray-300 mx-1"></div>
        <button type="button" onClick={() => handleCommand('justifyLeft')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Align Left"><AlignLeft size={16}/></button>
        <button type="button" onClick={() => handleCommand('justifyCenter')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Align Center"><AlignCenter size={16}/></button>
        <button type="button" onClick={() => handleCommand('justifyRight')} className="p-1.5 hover:bg-gray-200 rounded text-gray-600" title="Align Right"><AlignRight size={16}/></button>
      </div>
      <div
        ref={contentRef}
        contentEditable
        className="p-4 min-h-[150px] outline-none prose prose-sm max-w-none"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        dangerouslySetInnerHTML={{ __html: value }} 
      />
    </div>
  );
};

const Navigation = ({ activeTab, setActiveTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', id: 'home', icon: <Home size={18} /> },
    { name: 'About', id: 'about', icon: <Info size={18} /> },
    { name: 'Services', id: 'services', icon: <Briefcase size={18} /> },
    { name: 'Courses', id: 'courses', icon: <GraduationCap size={18} /> }, 
    { name: 'Blog', id: 'blog', icon: <BookOpen size={18} /> },
    { name: 'Contact', id: 'contact', icon: <Mail size={18} /> },
  ];

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' : 'bg-white py-4'}`}>
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setActiveTab('home')}>
            <img 
              src="/logo.png" 
              alt="Edvansolutions" 
              className="h-10 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                document.getElementById('fallback-logo').style.display = 'flex';
              }} 
            />
            <div id="fallback-logo" className="hidden flex items-center space-x-2 font-bold text-2xl tracking-tight text-slate-900">
              <Globe className="text-cyan-500" />
              <span>EDVAN<span className="text-cyan-500">SOLUTIONS</span></span>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'text-cyan-600 bg-cyan-50' 
                    : 'text-slate-500 hover:text-cyan-500 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </button>
            ))}
            <a 
              href="https://www.provixlearning.com/" 
              target="_blank" 
              rel="noreferrer"
              className="ml-4 flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-full text-sm font-medium hover:bg-slate-800 transition shadow-lg shadow-slate-200"
            >
              Learning Portal <ExternalLink size={14} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="text-slate-600 hover:text-cyan-600 transition">
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 pb-4 shadow-xl absolute w-full">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
              className={`block w-full text-left px-6 py-4 border-l-4 ${
                activeTab === item.id 
                  ? 'border-cyan-500 bg-cyan-50 text-cyan-700' 
                  : 'border-transparent text-slate-500 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-3">
                {item.icon}
                <span>{item.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};

const Hero = ({ setActiveTab }) => (
  <div className="relative bg-white pt-32 pb-24 overflow-hidden">
    {/* Modern Background Pattern */}
    <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30"></div>
    <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[500px] h-[500px] bg-cyan-50 rounded-full blur-[100px] opacity-60"></div>
    
    <div className="container mx-auto px-6 relative z-10">
      <div className="flex flex-col lg:flex-row items-center gap-16 mb-20">
        
        {/* Left Side: Text */}
        <div className="lg:w-1/2 space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 text-cyan-700 rounded-full text-xs font-bold uppercase tracking-wider border border-cyan-100">
            <Globe size={14} /> Premier Financial Consultancy
          </div>
          <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight text-slate-900 tracking-tight">
            Empowering <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">
              Future Finance Leaders
            </span>
          </h1>
          <p className="text-xl text-slate-500 leading-relaxed font-light max-w-xl">
            Bridging the gap between theory and practice with world-class virtual CFO services and elite training programs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <button 
              onClick={() => setActiveTab('contact')}
              className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-semibold transition-all shadow-lg shadow-cyan-200 hover:shadow-cyan-300 hover:-translate-y-1 flex items-center justify-center gap-2 group"
            >
              Get Started <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => setActiveTab('services')}
              className="px-8 py-4 bg-white hover:bg-gray-50 text-slate-700 rounded-full font-semibold transition-all border border-gray-200 hover:border-cyan-200 hover:shadow-md"
            >
              Our Solutions
            </button>
          </div>
        </div>

        {/* Right Side: Image */}
        <div className="lg:w-1/2 relative">
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white transform rotate-2 hover:rotate-0 transition-all duration-700">
             <img 
               src="/hero.jpg" 
               alt="Team working" 
               className="w-full h-auto object-cover"
               onError={(e) => {
                 e.target.src = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1632&ixlib=rb-4.0.3";
               }}
             />
             <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
             
             {/* Overlay Text */}
             <div className="absolute bottom-8 left-8 text-white">
                <p className="font-bold text-lg">Excellence in Execution</p>
                <p className="text-sm text-white/80">Transforming financial landscapes.</p>
             </div>
          </div>

          {/* Floating Cards */}
          <div className="absolute -bottom-8 -left-8 bg-white p-5 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-4 animate-bounce-slow max-w-xs">
            <div className="p-3 bg-green-50 text-green-600 rounded-xl">
              <TrendingUp size={24} />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase">Client Growth</p>
              <p className="text-lg font-bold text-slate-900">+127% YoY</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Full Width Stats */}
      {/* The stats section was missing in the Hero component, I've added it back in based on user feedback and previous context. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 pt-12 border-t border-gray-200">
          <div className="flex flex-col items-center p-6 bg-slate-50 rounded-2xl border border-transparent hover:border-cyan-100 hover:bg-white hover:shadow-md transition-all">
            <h4 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">30+</h4><p className="text-slate-500 font-medium uppercase tracking-wide text-xs md:text-sm">Global Clients</p>
          </div>
          <div className="flex flex-col items-center p-6 bg-slate-50 rounded-2xl border border-transparent hover:border-cyan-100 hover:bg-white hover:shadow-md transition-all">
            <h4 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">5+</h4><p className="text-slate-500 font-medium uppercase tracking-wide text-xs md:text-sm">Countries Served</p>
          </div>
          <div className="flex flex-col items-center p-6 bg-slate-50 rounded-2xl border border-transparent hover:border-cyan-100 hover:bg-white hover:shadow-md transition-all">
            <h4 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">100%</h4><p className="text-slate-500 font-medium uppercase tracking-wide text-xs md:text-sm">Compliance Rate</p>
          </div>
          <div className="flex flex-col items-center p-6 bg-slate-50 rounded-2xl border border-transparent hover:border-cyan-100 hover:bg-white hover:shadow-md transition-all">
            <h4 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">500+</h4><p className="text-slate-500 font-medium uppercase tracking-wide text-xs md:text-sm">Students Mentored</p>
          </div>
      </div>
    </div>
  </div>
);

const ServiceCard = ({ service }) => {
  const IconComponent = {
    'TrendingUp': TrendingUp, 'Users': Users, 'Briefcase': Briefcase, 'CheckCircle': CheckCircle, 'BookOpen': BookOpen, 'GraduationCap': GraduationCap
  }[service.icon] || Briefcase;

  return (
    <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-cyan-200 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-50 rounded-bl-[100px] -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
      <div className="relative z-10">
        <div className="w-14 h-14 bg-white border border-gray-100 rounded-xl flex items-center justify-center mb-6 shadow-sm group-hover:bg-cyan-500 group-hover:border-cyan-500 transition-colors duration-300">
          <IconComponent className="text-cyan-600 group-hover:text-white transition-colors duration-300" size={26} />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
        <div 
          className="text-slate-500 leading-relaxed font-light mb-6 text-sm prose prose-sm"
          dangerouslySetInnerHTML={{ __html: service.description }}
        ></div>
        <div className="flex items-center text-cyan-600 font-semibold text-sm gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
          Learn More <ArrowRight size={16} />
        </div>
      </div>
    </div>
  );
};

const CourseCard = ({ course }) => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 group flex flex-col h-full relative">
    <div className="h-2 bg-gradient-to-r from-cyan-400 to-blue-500"></div>
    <div className="p-8 flex-grow flex flex-col">
      <div className="mb-4">
        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
           <GraduationCap size={24} />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">{course.title}</h3>
        <div 
          className="text-slate-500 text-sm leading-relaxed prose prose-sm" 
          dangerouslySetInnerHTML={{ __html: course.description }}
        ></div>
      </div>
      <div className="mt-auto pt-6 border-t border-gray-50">
        {course.link ? (
            <a href={course.link} target="_blank" rel="noreferrer" className="text-sm font-bold text-cyan-600 flex items-center gap-2 hover:gap-3 transition-all">
                View Course Details <ArrowRight size={16} />
            </a>
        ) : (
            <span className="text-sm text-gray-400 font-medium">Coming Soon</span>
        )}
      </div>
    </div>
  </div>
);

const BlogCard = ({ blog }) => (
  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all group h-full flex flex-col">
    <div className="h-48 bg-gray-100 relative overflow-hidden">
      <div className="absolute inset-0 bg-cyan-900/10 group-hover:bg-transparent transition-colors"></div>
      {/* Placeholder for Blog Image - Use pattern if no image */}
      <div className="w-full h-full bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <BookOpen className="text-cyan-200" size={48} />
      </div>
    </div>
    <div className="p-6 flex-grow flex flex-col">
      <div className="flex items-center gap-2 text-xs text-cyan-600 font-semibold mb-3 uppercase tracking-wide">
        <Calendar size={12} /> {blog.date}
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">{blog.title}</h3>
      <div 
        className="text-slate-500 text-sm leading-relaxed mb-4 flex-grow prose prose-sm line-clamp-3"
        dangerouslySetInnerHTML={{ __html: blog.excerpt || blog.content.substring(0, 100) + '...' }}
      ></div>
      <button className="text-slate-900 font-semibold text-sm flex items-center gap-2 mt-auto group/btn">
        Read Article <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
      </button>
    </div>
  </div>
);

const TestimonialCard = ({ name, role, text }) => (
  <div className="bg-slate-50 p-8 rounded-2xl border border-transparent hover:border-cyan-100 hover:bg-white hover:shadow-lg transition-all h-full flex flex-col justify-between">
    <div>
      <Quote className="text-cyan-200 mb-4" size={32} />
      <p className="text-slate-600 italic mb-6 leading-relaxed">"{text}"</p>
    </div>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center text-cyan-700 font-bold shrink-0">{name[0]}</div>
      <div><h5 className="font-bold text-slate-900 text-sm">{name}</h5><p className="text-xs text-slate-500 uppercase">{role}</p></div>
    </div>
  </div>
);

// --- Render About Section (Added Back) ---
const renderAbout = (content) => (
    <div className="py-24 bg-white min-h-screen">
      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center mb-20">
          <div className="lg:w-1/2">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">{content.about.title}</h1>
            <div className="prose lg:prose-lg text-slate-500 font-light leading-relaxed whitespace-pre-wrap mb-8" dangerouslySetInnerHTML={{ __html: content.about.description }}></div>
            <div className="bg-cyan-50 p-8 rounded-2xl border border-cyan-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-100 rounded-full blur-[50px] opacity-50"></div>
              <h3 className="font-bold text-cyan-900 mb-2 flex items-center gap-2 relative z-10"><Award size={20}/> Our Mission</h3>
              <div className="text-cyan-700 italic relative z-10" dangerouslySetInnerHTML={{ __html: content.about.mission }}></div>
            </div>
          </div>
          <div className="lg:w-1/2 w-full relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
               <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1000" alt="Office Meeting" className="w-full h-auto object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3 animate-bounce-slow z-20">
                <div className="bg-green-50 p-3 rounded-lg text-green-600"><Clock size={20}/></div>
                <div><p className="text-xs font-bold text-gray-400 uppercase">Experience</p><p className="font-bold text-slate-900 text-lg">10+ Years</p></div>
             </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full">
            <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-cyan-100">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-2">{content.about.stat1_val}</div><div className="text-sm text-slate-500 font-bold uppercase tracking-wider text-center">{content.about.stat1_lbl}</div>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-cyan-100">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-2">{content.about.stat2_val}</div><div className="text-sm text-slate-500 font-bold uppercase tracking-wider text-center">{content.about.stat2_lbl}</div>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-cyan-100">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-2">{content.about.stat3_val}</div><div className="text-sm text-slate-500 font-bold uppercase tracking-wider text-center">{content.about.stat3_lbl}</div>
            </div>
            <div className="flex flex-col items-center justify-center p-8 bg-slate-50 rounded-2xl hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-cyan-100">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-2">{content.about.stat4_val}</div><div className="text-sm text-slate-500 font-bold uppercase tracking-wider text-center">{content.about.stat4_lbl}</div>
            </div>
        </div>
      </div>
    </div>
);

// --- Main App ---

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [user, setUser] = useState(null);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState(DEFAULT_BLOGS);
  const [testimonials, setTestimonials] = useState([]); 
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  
  const [editMode, setEditMode] = useState(null); 
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (window.location.pathname === '/admin') setActiveTab('admin');
    if (!auth) { setLoading(false); return; }
    const initAuth = async () => {
      try { await signInAnonymously(auth); } catch (err) { console.warn("Auth failed:", err); setAuthError(err.message); setLoading(false); }
    };
    initAuth();
    return onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!user || !db) return;
    const unsubContent = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'content'), s => {
      const c = { ...DEFAULT_CONTENT }; s.forEach(d => { if(c[d.id]) c[d.id] = d.data(); }); setContent(c); setLoading(false);
    });
    const unsubServices = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'services'), s => {
      const arr = []; s.forEach(d => arr.push({id: d.id, ...d.data()})); if(arr.length) setServices(arr);
    });
    const unsubCourses = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'courses'), s => {
      const arr = []; s.forEach(d => arr.push({id: d.id, ...d.data()})); setCourses(arr);
    });
    const unsubBlogs = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'blogs'), s => {
      const arr = []; s.forEach(d => arr.push({id: d.id, ...d.data()})); if(arr.length) setBlogs(arr);
    });
    const unsubTestimonials = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'testimonials'), s => {
      const arr = []; s.forEach(d => arr.push({id: d.id, ...d.data()})); setTestimonials(arr);
    });
    const unsubEnq = onSnapshot(query(collection(db, 'artifacts', appId, 'public', 'data', 'enquiries'), orderBy('timestamp', 'desc')), s => {
      const arr = []; s.forEach(d => arr.push({id: d.id, ...d.data()})); setEnquiries(arr);
    });
    return () => { unsubContent(); unsubServices(); unsubCourses(); unsubBlogs(); unsubTestimonials(); unsubEnq(); };
  }, [user]);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!user || !db) return alert("Database Not connected.");
    const fd = new FormData(e.target);
    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'enquiries'), {
        name: fd.get('name'), email: fd.get('email'), phone: fd.get('phone'), message: fd.get('message'), timestamp: new Date().toISOString()
      });
      alert('Message sent!'); e.target.reset();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPassword === 'admin123') { setIsAdminLoggedIn(true); setAdminPassword(''); } else alert('Wrong password.');
  };

  const saveDoc = async (coll, id, data) => {
    if (!user) return alert("Not authenticated.");
    setIsSaving(true);
    try {
      if (id) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', coll, id), data, { merge: true });
      else await addDoc(collection(db, 'artifacts', appId, 'public', 'data', coll), data);
      setEditMode(null); setIsSaving(false);
    } catch(e) { console.error(e); setIsSaving(false); alert(`Save failed: ${e.message}`); }
  };

  const deleteItem = async (coll, id) => {
    if(!window.confirm("Delete item?")) return;
    if (!user) return alert("Not connected.");
    await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', coll, id));
  };

  const handleLoadDefaults = async () => {
    if (!user) return alert("Not authenticated.");
    setIsSaving(true);
    try {
        for (const t of SAMPLE_TESTIMONIALS) await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'testimonials'), t);
        setIsSaving(false); alert("Samples loaded!");
    } catch (e) { setIsSaving(false); alert("Error: " + e.message); }
  };

  const renderHome = () => (
    <>
      <Hero setActiveTab={setActiveTab} />
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 text-center max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Redefining Financial Leadership</h2>
          <p className="text-lg text-slate-500 leading-relaxed font-light">
            At Edvansolutions, we combine deep industry expertise with cutting-edge educational methodologies.
          </p>
        </div>
      </section>
      <section className="py-24 bg-slate-50 relative">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex justify-between items-end mb-12">
            <div><h2 className="text-3xl font-bold text-slate-900 mb-2">Our Expertise</h2><p className="text-slate-500">Holistic solutions for modern challenges.</p></div>
            <button onClick={() => setActiveTab('services')} className="hidden md:flex text-cyan-600 font-semibold gap-2 items-center hover:gap-3 transition-all">View All Services <ArrowRight size={16}/></button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 4).map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        </div>
      </section>
      <section className="py-24 bg-white relative" id="courses">
        <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <h1 className="text-4xl font-bold text-slate-900 mb-6">Our Courses</h1>
                <p className="text-slate-500 text-lg">Elevate your career with our expert-led financial training programs.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-blue-50 text-slate-900 p-8 rounded-2xl shadow-xl flex flex-col justify-between relative overflow-hidden h-full border border-blue-100">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-blue-100 rounded-full blur-[80px] opacity-50"></div>
                    <div className="relative z-10 flex-grow">
                        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm"><BookOpen className="text-blue-600" size={26} /></div>
                        <h3 className="text-2xl font-bold mb-3">Provix Learning LMS</h3>
                        <p className="text-slate-600 mb-6 font-light leading-relaxed">Access our premier Learning Management System for comprehensive financial courses.</p>
                    </div>
                    <a href="https://www.provixlearning.com/" target="_blank" rel="noreferrer" className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl text-center hover:bg-blue-500 transition relative z-10 flex items-center justify-center gap-2 shadow-lg shadow-blue-200">Access LMS Portal <ExternalLink size={18} /></a>
                </div>
                {courses.map(course => <CourseCard key={course.id} course={course} />)}
            </div>
        </div>
      </section>
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-16">Trusted by Industry Leaders</h2>
          {testimonials.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">{testimonials.map(t => <TestimonialCard key={t.id} name={t.name} role={t.role} text={t.text} />)}</div>
          ) : <p className="text-center text-gray-400 italic">No testimonials yet.</p>}
        </div>
      </section>
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-[120px] opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Elevate Your Financial Strategy?</h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto text-lg">Join hundreds of professionals.</p>
          <div className="flex justify-center gap-4">
            <button onClick={() => setActiveTab('contact')} className="px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white rounded-full font-bold shadow-lg shadow-cyan-500/30 transition">Get in Touch</button>
          </div>
        </div>
      </section>
    </>
  );

  const renderServices = () => (
    <div className="py-24 bg-slate-50 min-h-screen">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-4xl font-bold text-slate-900 mb-6">Our Services</h1>
          <p className="text-slate-500 text-lg">Comprehensive financial solutions tailored for growth.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map(s => <ServiceCard key={s.id} service={s} />)}
        </div>
      </div>
    </div>
  );

  const renderCourses = () => (
    <section className="py-24 bg-white min-h-screen" id="courses">
        <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <h1 className="text-4xl font-bold text-slate-900 mb-6">Our Courses</h1>
                <p className="text-slate-500 text-lg">Elevate your career with our expert-led programs.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-blue-50 text-slate-900 p-8 rounded-2xl shadow-xl flex flex-col justify-between relative overflow-hidden h-full border border-blue-100">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-blue-100 rounded-full blur-[80px] opacity-50"></div>
                    <div className="relative z-10 flex-grow">
                        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm"><BookOpen className="text-blue-600" size={26} /></div>
                        <h3 className="text-2xl font-bold mb-3">Provix Learning LMS</h3>
                        <p className="text-slate-600 mb-6 font-light leading-relaxed">Access our premier Learning Management System.</p>
                    </div>
                    <a href="https://www.provixlearning.com/" target="_blank" rel="noreferrer" className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl text-center hover:bg-blue-500 transition relative z-10 flex items-center justify-center gap-2 shadow-lg shadow-blue-200">Access LMS Portal <ExternalLink size={18} /></a>
                </div>
                {courses.map(course => <CourseCard key={course.id} course={course} />)}
            </div>
        </div>
    </section>
  );

  const renderBlog = () => (
    <div className="py-24 bg-white min-h-screen">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16"><h1 className="text-4xl font-bold text-slate-900 mb-4">Insights & News</h1><p className="text-slate-500">Latest updates.</p></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{blogs.map(b => <BlogCard key={b.id} blog={b} />)}</div>
      </div>
    </div>
  );

  const renderContact = () => (
    <div className="py-24 bg-slate-50 min-h-screen">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-6">Let's Start a Conversation</h1>
            <div className="space-y-6">
              <div className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100"><div className="bg-cyan-50 p-3 rounded-full text-cyan-600"><Mail size={24}/></div><div><p className="text-sm text-slate-400 font-bold uppercase">Email Us</p><p className="text-slate-900 font-medium">{content.contact.email}</p></div></div>
              <div className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100"><div className="bg-cyan-50 p-3 rounded-full text-cyan-600"><Users size={24}/></div><div><p className="text-sm text-slate-400 font-bold uppercase">Call Us</p><p className="text-slate-900 font-medium">{content.contact.phone}</p></div></div>
              <div className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100"><div className="bg-cyan-50 p-3 rounded-full text-cyan-600"><Globe size={24}/></div><div><p className="text-sm text-slate-400 font-bold uppercase">Visit Us</p><p className="text-slate-900 font-medium">{content.contact.address}</p></div></div>
            </div>
          </div>
          <form onSubmit={handleContact} className="bg-white p-10 rounded-3xl shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-slate-900 mb-8">Send a Message</h3>
            <div className="space-y-5">
              <input required name="name" className="w-full p-4 bg-slate-50 border border-gray-200 rounded-xl focus:border-cyan-500 outline-none" placeholder="Your Name" />
              <input required name="email" type="email" className="w-full p-4 bg-slate-50 border border-gray-200 rounded-xl focus:border-cyan-500 outline-none" placeholder="Email Address" />
              <input required name="phone" className="w-full p-4 bg-slate-50 border border-gray-200 rounded-xl focus:border-cyan-500 outline-none" placeholder="Phone Number" />
              <textarea required name="message" rows="4" className="w-full p-4 bg-slate-50 border border-gray-200 rounded-xl focus:border-cyan-500 outline-none" placeholder="How can we help?"></textarea>
              <button type="submit" className="w-full bg-cyan-500 text-white py-4 rounded-xl font-bold hover:bg-cyan-600 transition shadow-lg shadow-cyan-200">Send Message</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderAdmin = () => {
    if (!isAdminLoggedIn) return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <form onSubmit={handleAdminLogin} className="bg-white p-10 rounded-2xl shadow-xl max-w-sm w-full">
          <h2 className="text-2xl font-bold text-center mb-6">Admin Access</h2>
          <input type="password" value={adminPassword} onChange={e=>setAdminPassword(e.target.value)} className="w-full p-3 border rounded-lg mb-4" placeholder="Password" />
          <button className="w-full bg-slate-900 text-white py-3 rounded-lg font-bold">Login</button>
        </form>
      </div>
    );

    return (
      <div className="min-h-screen bg-slate-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
            <button onClick={()=>setIsAdminLoggedIn(false)} className="text-red-500 font-bold">Logout</button>
          </div>
          <div className="grid gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><Edit2 size={20}/> Site Content</h2>
              <div className="flex gap-4">
                <button onClick={() => { setEditMode('about'); setEditForm(content.about); }} className="px-4 py-2 border rounded hover:bg-slate-50">Edit About</button>
                <button onClick={() => { setEditMode('contact'); setEditForm(content.contact); }} className="px-4 py-2 border rounded hover:bg-slate-50">Edit Contact</button>
              </div>
              {editMode && !['service_add','blog_add','testimonial_add','course_add','service_edit','course_edit','blog_edit','testimonial_edit'].includes(editMode) && (
                <div className="mt-6 p-6 bg-slate-50 rounded-xl">
                  <h3 className="font-bold mb-4 capitalize">Edit {editMode}</h3>
                  {Object.keys(editForm).map(k => (
                    <div key={k} className="mb-4">
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">{k}</label>
                      {(k === 'description' || k === 'mission' || k === 'content' || k === 'excerpt') ? (
                         <RichTextEditor value={editForm[k]} onChange={(val) => setEditForm({...editForm, [k]: val})} />
                      ) : (
                        <input className="w-full p-2 border rounded" value={editForm[k]} onChange={e=>setEditForm({...editForm, [k]: e.target.value})} />
                      )}
                    </div>
                  ))}
                  <button onClick={() => saveDoc('content', editMode, editForm)} disabled={isSaving} className={`px-4 py-2 rounded text-white ${isSaving ? 'bg-gray-400' : 'bg-green-600'}`}>{isSaving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => setEditMode(null)} className="ml-2 text-gray-500">Cancel</button>
                </div>
              )}
            </div>
            
            {/* Courses Admin */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex justify-between mb-6"><h2 className="text-xl font-bold flex items-center gap-2"><GraduationCap size={20}/> Manage Courses</h2><button onClick={() => { setEditMode('course_add'); setEditForm({title:'', description:'', link:''}); }} className="bg-cyan-600 text-white px-3 py-1 rounded text-sm font-bold flex gap-1 items-center"><Plus size={16}/> Add Course</button></div>
                {(editMode === 'course_add' || editMode === 'course_edit') && (
                    <div className="mb-6 p-6 bg-cyan-50 rounded-xl">
                        <h3 className="font-bold mb-4">{editMode === 'course_add' ? 'New Course' : 'Edit Course'}</h3>
                        <input className="w-full p-2 border rounded mb-2" placeholder="Course Title" value={editForm.title} onChange={e=>setEditForm({...editForm, title: e.target.value})} />
                        <div className="mb-2"><RichTextEditor value={editForm.description} onChange={(val)=>setEditForm({...editForm, description: val})} placeholder="Description" /></div>
                        <input className="w-full p-2 border rounded mb-2" placeholder="Course Link (Optional)" value={editForm.link} onChange={e=>setEditForm({...editForm, link: e.target.value})} />
                        <button onClick={() => saveDoc('courses', editForm.id || null, editForm)} className="bg-cyan-600 text-white px-4 py-2 rounded">{editMode === 'course_add' ? 'Add' : 'Update'}</button>
                        <button onClick={() => setEditMode(null)} className="ml-2 text-gray-500">Cancel</button>
                    </div>
                )}
                <div className="space-y-2">
                    {courses.map(c => (
                        <div key={c.id} className="flex justify-between items-center p-4 border rounded hover:bg-slate-50">
                            <div><h4 className="font-bold">{c.title}</h4></div>
                            <div className="flex gap-2">
                                <button onClick={() => { setEditMode('course_edit'); setEditForm(c); }} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><Edit2 size={18}/></button>
                                <button onClick={() => deleteItem('courses', c.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={18}/></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Blogs Admin */}
             <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex justify-between mb-6"><h2 className="text-xl font-bold flex items-center gap-2"><BookOpen size={20}/> Manage Blogs</h2><button onClick={() => { setEditMode('blog_add'); setEditForm({title:'', excerpt:'', content:'', date: new Date().toLocaleDateString(), author:'Admin'}); }} className="bg-cyan-600 text-white px-3 py-1 rounded text-sm font-bold flex gap-1 items-center"><Plus size={16}/> Add Post</button></div>
              {(editMode === 'blog_add' || editMode === 'blog_edit') && (
                <div className="mb-6 p-6 bg-cyan-50 rounded-xl">
                  <h3 className="font-bold mb-4">{editMode === 'blog_add' ? 'New Blog Post' : 'Edit Blog Post'}</h3>
                  <input className="w-full p-2 border rounded mb-2" placeholder="Title" value={editForm.title} onChange={e=>setEditForm({...editForm, title: e.target.value})} />
                  <div className="mb-2"><label className="text-xs font-bold text-gray-500">Excerpt</label><RichTextEditor value={editForm.excerpt} onChange={(val)=>setEditForm({...editForm, excerpt: val})} /></div>
                  <div className="mb-2"><label className="text-xs font-bold text-gray-500">Content</label><RichTextEditor value={editForm.content} onChange={(val)=>setEditForm({...editForm, content: val})} /></div>
                  <button onClick={() => saveDoc('blogs', editForm.id || null, editForm)} className="bg-cyan-600 text-white px-4 py-2 rounded">{editMode === 'blog_add' ? 'Publish' : 'Update'}</button>
                  <button onClick={() => setEditMode(null)} className="ml-2 text-gray-500">Cancel</button>
                </div>
              )}
               <div className="space-y-2">{blogs.map(b => (<div key={b.id} className="flex justify-between items-center p-4 border rounded hover:bg-slate-50"><div><h4 className="font-bold">{b.title}</h4></div><div className="flex gap-2"><button onClick={() => { setEditMode('blog_edit'); setEditForm(b); }} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><Edit2 size={18}/></button><button onClick={() => deleteItem('blogs', b.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={18}/></button></div></div>))}</div>
            </div>

            {/* Services Admin */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex justify-between mb-6"><h2 className="text-xl font-bold flex items-center gap-2"><Briefcase size={20}/> Manage Services</h2><button onClick={() => { setEditMode('service_add'); setEditForm({title:'', description:'', icon:'Briefcase'}); }} className="bg-cyan-600 text-white px-3 py-1 rounded text-sm font-bold flex gap-1 items-center"><Plus size={16}/> Add Service</button></div>
                {(editMode === 'service_add' || editMode === 'service_edit') && (
                    <div className="mb-6 p-6 bg-cyan-50 rounded-xl">
                    <h3 className="font-bold mb-4">{editMode === 'service_add' ? 'New Service' : 'Edit Service'}</h3>
                    <input className="w-full p-2 border rounded mb-2" placeholder="Title" value={editForm.title} onChange={e=>setEditForm({...editForm, title: e.target.value})} />
                    <div className="mb-2"><RichTextEditor value={editForm.description} onChange={(val)=>setEditForm({...editForm, description: val})} placeholder="Description" /></div>
                    <button onClick={() => saveDoc('services', editForm.id || null, editForm)} className="bg-cyan-600 text-white px-4 py-2 rounded">{editMode === 'service_add' ? 'Add' : 'Update'}</button>
                    <button onClick={() => setEditMode(null)} className="ml-2 text-gray-500">Cancel</button>
                    </div>
                )}
                <div className="space-y-2">{services.map(s => (<div key={s.id} className="flex justify-between items-center p-4 border rounded hover:bg-slate-50"><div><h4 className="font-bold">{s.title}</h4></div><div className="flex gap-2"><button onClick={() => { setEditMode('service_edit'); setEditForm(s); }} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><Edit2 size={18}/></button><button onClick={() => deleteItem('services', s.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={18}/></button></div></div>))}</div>
            </div>
            
            {/* Testimonials Admin */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="flex justify-between mb-6"><h2 className="text-xl font-bold flex items-center gap-2"><Quote size={20}/> Manage Testimonials</h2><div className="flex gap-2">{testimonials.length === 0 && (<button onClick={handleLoadDefaults} className="bg-slate-200 text-slate-700 px-3 py-1 rounded text-sm font-bold flex gap-1 items-center hover:bg-slate-300"><RefreshCw size={16}/> Load Sample Data</button>)}<button onClick={() => { setEditMode('testimonial_add'); setEditForm({name:'', role:'', text:''}); }} className="bg-cyan-600 text-white px-3 py-1 rounded text-sm font-bold flex gap-1 items-center"><Plus size={16}/> Add New</button></div></div>
              {(editMode === 'testimonial_add' || editMode === 'testimonial_edit') && (
                <div className="mb-6 p-6 bg-cyan-50 rounded-xl">
                  <h3 className="font-bold mb-4">{editMode === 'testimonial_add' ? 'New Testimonial' : 'Edit Testimonial'}</h3>
                  <input className="w-full p-2 border rounded mb-2" placeholder="Client Name" value={editForm.name} onChange={e=>setEditForm({...editForm, name: e.target.value})} />
                  <input className="w-full p-2 border rounded mb-2" placeholder="Role / Company" value={editForm.role} onChange={e=>setEditForm({...editForm, role: e.target.value})} />
                  <textarea className="w-full p-2 border rounded mb-2" rows={3} placeholder="Quote Text" value={editForm.text} onChange={e=>setEditForm({...editForm, text: e.target.value})} />
                  <button onClick={() => saveDoc('testimonials', editForm.id || null, editForm)} className="bg-cyan-600 text-white px-4 py-2 rounded">{editMode === 'testimonial_add' ? 'Add' : 'Update'}</button>
                  <button onClick={() => setEditMode(null)} className="ml-2 text-gray-500">Cancel</button>
                </div>
              )}
               <div className="space-y-2">{testimonials.map(t => (<div key={t.id} className="flex justify-between items-center p-4 border rounded hover:bg-slate-50"><div><h4 className="font-bold">{t.name}</h4><p className="text-xs text-gray-500">{t.role}</p></div><div className="flex gap-2"><button onClick={() => { setEditMode('testimonial_edit'); setEditForm(t); }} className="text-blue-500 hover:bg-blue-50 p-2 rounded"><Edit2 size={18}/></button><button onClick={() => deleteItem('testimonials', t.id)} className="text-red-500 hover:bg-red-50 p-2 rounded"><Trash2 size={18}/></button></div></div>))}</div>
            </div>

             <div className="bg-white p-8 rounded-xl shadow-sm">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><Mail size={20}/> Inbox ({enquiries.length})</h2>
              {enquiries.map(e => (
                <div key={e.id} className="p-4 border-b last:border-0 hover:bg-slate-50">
                  <div className="flex justify-between mb-1"><span className="font-bold">{e.name}</span><span className="text-xs text-gray-500">{new Date(e.timestamp).toLocaleDateString()}</span></div>
                  <div className="text-sm text-cyan-600 mb-2">{e.email} | {e.phone}</div>
                  <p className="text-slate-600 text-sm">{e.message}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="h-screen flex items-center justify-center text-cyan-600 font-bold animate-pulse">Loading...</div>;

  return (
    <div className="font-sans text-slate-900 bg-white min-h-screen flex flex-col selection:bg-cyan-100 selection:text-cyan-900">
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-grow">
        {activeTab === 'home' && renderHome()}
        {activeTab === 'about' && renderAbout(content)}
        {activeTab === 'services' && renderServices()}
        {activeTab === 'courses' && renderCourses()}
        {activeTab === 'blog' && renderBlog()}
        {activeTab === 'contact' && renderContact()}
        {activeTab === 'admin' && renderAdmin()}
      </main>
      <footer className="bg-white border-t border-gray-100 py-16">
        <div className="container mx-auto px-6 grid md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-6 font-bold text-xl tracking-tight">EDVAN<span className="text-cyan-500">SOLUTIONS</span></div>
            <p className="text-slate-500 max-w-xs leading-relaxed">Empowering the next generation of financial leaders with practical skills and global opportunities.</p>
          </div>
          <div>
            <h5 className="font-bold text-slate-900 mb-6">Explore</h5>
            <ul className="space-y-3 text-slate-500">
              <li><button onClick={() => setActiveTab('home')} className="hover:text-cyan-600">Home</button></li>
              <li><button onClick={() => setActiveTab('about')} className="hover:text-cyan-600">About</button></li>
              <li><button onClick={() => setActiveTab('services')} className="hover:text-cyan-600">Services</button></li>
              <li><button onClick={() => setActiveTab('courses')} className="hover:text-cyan-600">Courses</button></li>
              <li><button onClick={() => setActiveTab('blog')} className="hover:text-cyan-600">Blog</button></li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold text-slate-900 mb-6">Learning</h5>
            <a href="https://www.provixlearning.com/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-500 hover:text-cyan-600 transition">
              <BookOpen size={16}/> Provix LMS
            </a>
          </div>
        </div>
        <div className="text-center text-slate-400 text-sm mt-16 pt-8 border-t border-gray-50">
          &copy; {new Date().getFullYear()} Edvansolutions. All rights reserved.
        </div>
      </footer>
    </div>
  );
}